import { DndContext, DragOverlay } from "@dnd-kit/core";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import { Requirement, CourseSet, Select } from "../../types/requirement.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { useCourseSetDnd } from "../../hooks/useCourseSetDnd";
import HeaderListSlots from "./HeaderListSlots";
import HeaderGroupSlots from "./HeaderGroupSlots";
import RemoveDropZone from "../shared/RemoveDropZone";

interface HeaderCompletedCoursesProps {
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    onAdd: (course: AddableCourse, nodeId: string | null) => void | Promise<void>;
    onDelete: (course: CourseLite) => void | Promise<void>;
    onOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
    /** 只渲染某个子节点的槽位(用于 OPTION/OPTION_GROUP 里单个 option);默认渲染整个 requirement 根节点 */
    node?: CourseSet | Select;
}

export default function HeaderCompletedCourses({
    requirement,
    coursesMap,
    onAdd,
    onDelete,
    onOverride,
    node,
}: HeaderCompletedCoursesProps) {
    const { activeDrag, handleDragStart, handleDragEnd, handleDragCancel } = useCourseSetDnd(requirement, onOverride);

    const removeDropZone = (
        <RemoveDropZone
            id={`header-remove-${requirement.info.id}`}
            isVisible={activeDrag?.isApplied === true}
            layout="horizontal"
        />
    );

    const renderSlots = () => {
        // 默认整个 requirement 根节点;OPTION 类传入单个 option 节点只渲染它
        const target: CourseSet | Select = node ?? requirement.rootNode;
        switch (target.type) {
            case "COURSE_SET":
                return (
                    <HeaderListSlots
                        courseSet={target}
                        coursesMap={coursesMap}
                        requirementId={requirement.info.id}
                        onAdd={onAdd}
                        onDelete={onDelete}
                        removeDropZone={removeDropZone}
                        isBroad={requirement.info.isBroadRequirement}
                    />
                );
            case "SELECT":
                return (
                    <HeaderGroupSlots
                        rootNode={target}
                        requirement={requirement}
                        requirementId={requirement.info.id}
                        coursesMap={coursesMap}
                        onAdd={onAdd}
                        onDelete={onDelete}
                        removeDropZone={removeDropZone}
                        isBroad={requirement.info.isBroadRequirement}
                    />
                );
        }
    };

    return (
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            {renderSlots()}
            <DragOverlay>
                {activeDrag ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-white shadow-lg px-2.5 py-1 text-xs font-medium text-gray-700 cursor-grabbing">
                        {activeDrag.course.courseInfo.subject} {activeDrag.course.courseInfo.number}
                    </span>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
